<template>
  <div class="overflow-y-hidden relative w-[100lvw] h-[100lvh] bg-cw-green flex flex-col" :class="{ 'card-selection-mode': isSelectingCards }">
    <img src="@/assets/images/background.webp" alt="Background" class="absolute inset-0 w-full h-full object-cover opacity-35" />

    <div class="relative text-white px-6 md:px-8 lg:px-10 py-5 md:py-6 lg:py-8 flex items-start justify-between w-full">
      <ScoreBox />

      <OpponentsCards
        :message="gameActionMessage"
        :opponents="otherPlayersProfiles"
        :messagePlayerID="gameActionPlayerID"
        :currentPlayer="currentTurnPlayerProfile"
        class="absolute left-0 right-0 w-max mx-auto"
      />

      <div class="flex lg:max-xl:flex-col items-center gap-2 lg:gap-3">
        <GameTimer
          v-if="currentTurnPlayerProfile && currentTurnPlayerProfile?.id != userPlayerProfile?.id && currentTurnPlayerProfile.actionTimeout > 0"
          :shouldIntensify="false"
          :key="currentTurnPlayerProfile.actionTimeout"
          :timeRemaining="currentTurnPlayerProfile.actionTimeout"
        />
        <PlayPauseButton class="lg:max-xl:-order-1" />
      </div>
    </div>
    <div class="absolute bottom-[45.6%] w-full px-6 md:px-8 lg:px-10 flex items-end justify-between">
      <div class="flex flex-col items-center relative">
        <StockPile class="relative" :isEmpty="!activeDeck?.length">
          <p class="text-sm md:text-base lg:text-xl xl:text-2xl font-bold text-white">{{ activeDeck?.length }}</p>
          <CardBack alt="" class="absolute inset-0 w-full h-full rounded-[3px] object-cover opacity-15" />
        </StockPile>
        <span class="mt-2 tracking-[.001em] absolute top-[100%] text-[#B2E6D3] font-medium">Pile</span>
      </div>
      <GameStatus />
      <div class="flex flex-col items-center relative">
        <StockPile
          class="!p-0 cursor-pointer"
          @cardDropped="handleCardDropped"
          :acceptDrops="canDragAndDrop"
          :class="{ 'cursor-not-allowed!': isWaitingTurn || !activeDeck?.length }"
          @click="initRandomCardExchange"
        >
          <SwapSign class="xl:w-10 xl:h-10 lg:h-6 lg:w-6 h-5 w-5" />
        </StockPile>
        <span class="mt-2 tracking-[.001em] absolute top-[100%] text-[#B2E6D3] font-medium">Exchange</span>
      </div>
    </div>

    <!-- Custom screen for selecting cards when trying to trade card or exchange for a random card -->
    <Transition
      enter-active-class="transition-all duration-300 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-all duration-200 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div v-if="isSelectingCards" class="absolute z-3 inset-0 bg-cw-dark-alt/85 flex flex-col items-center justify-end">
        <div class="relative min-w-96 max-w-max bg-cw-dark rounded-[13px] px-10 pt-10 pb-12">
          <button @click="goToGameAreaScreen" v-if="canCloseTradeCardOptions" class="absolute top-5 right-5">
            <CloseIcon class="text-white" />
          </button>
          <template v-if="isTradingRandomCard">
            <div class="mt-5">
              <div class="flex flex-col items-center">
                <h1 class="text-3xl font-bold text-white mb-4">Exchange for a random card</h1>

                <BaseButton @click="exchangeCard" v-if="cardToExchange" background-color="var(--color-cw-olive)" border-color="var(--color-cw-green-border-shadow)">
                  <p class="w-full heightSm:!text-sm !text-base xl:!text-xl">Exchange card</p>
                </BaseButton>
                <h5 class="font-semibold text-cw-grey-2 mt-2 text-xl" v-else>Select a card to exchange</h5>
              </div>
            </div>
          </template>
          <template v-if="isSelectingTradeCard">
            <div class="mt-5">
              <div class="flex flex-col items-center">
                <h1 class="text-3xl font-bold text-white">Trade a card</h1>

                <h5 class="font-semibold text-cw-grey-2 mt-2 text-xl">Select the card you want to trade</h5>
              </div>
            </div>
          </template>
        </div>

        <div class="player-hand mt-[10vh] mb-[5vh]">
          <div class="p-4">
            <PlayersHand
              :cursor="isSelectingCards ? 'pointer' : 'default'"
              :animateCardHover="isSelectingCards"
              @cardClick="handleCardClicked"
              :cards="userPlayersHandArranged!"
              :disabledCards="cardToExchange ? userPlayersHandArranged?.filter(card => card.id != cardToExchange?.id) : []"
              :isExpanded="isTurn"
              :canDragDrop="false"
              :canRearrange="canRearrangeCards"
            />
          </div>
        </div>
      </div>
    </Transition>

    <div class="player-hand absolute bottom-4 lg:bottom-12 left-1/2 -translate-x-1/2 w-full px-6 md:px-8 lg:px-10 flex justify-center" v-if="!isSelectingCards">
      <div class="flex gap-x-2 lg:gap-x-3 items-center">
        <GameTimer
          :key="userPlayerProfile.actionTimeout"
          :timeRemaining="getActionTimeout()"
          v-if="userPlayerProfile && userPlayerProfile.actionTimeout > 0"
          :gameId="gameState?.id"
          @cardsReordered="handleCardsReordered"
          class="absolute left-6 md:left-8 lg:left-10 lg:max-xl:bottom-[calc(100%+12px)]"
        />
        <PlayersHand
          :cursor="isSelectingCards ? 'pointer' : 'default'"
          :cards="userPlayersHandArranged!"
          :isExpanded="isTurn"
          :canDragDrop="canDragAndDrop"
          :canRearrange="canRearrangeCards"
          :gameId="gameState?.id"
          @cardClick="openCard"
          @cardsReordered="handleCardsReordered"
        />
      </div>
    </div>

    <CardViewModal ref="cardViewModalRef" />
  </div>
</template>

<script setup lang="ts">
import PlayPauseButton from '@/components/game-play/PlayPauseButton.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import ScoreBox from '@/components/game-play/ScoreBox.vue'
import StockPile from '@/components/game-play/StockPile.vue'
import OpponentsCards from '@/components/game-play/OpponentsCards.vue'
import SwapSign from '@/assets/svgs/others/swap.svg'
import CardBack from '@/assets/svgs/cards/cardback.svg'
import GameStatus from '@/components/game-play/GameStatus.vue'
import GameTimer from '@/components/interface/GameTimer.vue'
import PlayersHand from '@/components/game-play/PlayersHand.vue'
// import backgroundImage from '@/assets/images/background.webp'
import { useGameStore } from '@/stores/game'
import { storeToRefs } from 'pinia'
import { useGameArea } from '@/composables/useGameArea'
import { computed, inject, onBeforeUnmount, onMounted, ref, useTemplateRef, watch } from 'vue'
import type { CardT } from '@/types/Game'
import CloseIcon from '@/assets/svgs/icons/close.svg'
import { useHelpers } from '@/composables/useHelpers'
import { GameEvent } from '@/types/Events'
import useSocket from '@/composables/useSocket'
import { useAppStore } from '@/stores/app'
import { useQueue } from '@/composables/useQueue'
import { useSound } from '@/composables/useSound'
import CardViewModal from '@/components/modals/auth/CardViewModal.vue'

const showLoader = inject('showLoader') as InjectedFunctionT
const hideLoader = inject('hideLoader') as InjectedFunctionT

const socket = useSocket()
const { handleCustomError, sleep } = useHelpers()
const { deferGameState } = useAppStore()
const { resumeGameState, getActionTimeout, arrangePlayerHand } = useGameStore()
const { activeGameAreaScreen, setActiveGameAreaScreen, goToGameAreaScreen, cardToExchange, cardToTrade, canCloseTradeCardOptions } = useGameArea()
const { activateQueue, deactivateQueue } = useQueue()
const { playTrashCard } = useSound()
const cardViewModalRef = useTemplateRef('cardViewModalRef')

const isSelectingCards = computed(() => isTradingRandomCard.value || isSelectingTradeCard.value)
const isTradingRandomCard = computed(() => activeGameAreaScreen.value === 'RANDOM_CARD_EXCHANGE_SELECTION_SCREEN')
const isSelectingTradeCard = computed(() => activeGameAreaScreen.value === 'TRADE_CARD_SELECTION_SCREEN')
const canDragAndDrop = computed(() => isTurn.value && !isWaitingTurn.value && !!activeDeck?.value?.length && !isSelectingCards.value)
const canRearrangeCards = computed(() => !isSelectingCards.value)

const { otherPlayersProfiles, userPlayersHandArranged, isTurn, activeDeck, isWaitingTurn, userPlayerProfile, currentTurnPlayerProfile, lastGameAction, lastGamePlayerID, gameState } = storeToRefs(useGameStore())

// Detect any opponent who has gone offline (server sets reconnectTimeout !== -1 when a player disconnects)
const offlineOpponent = computed(() => otherPlayersProfiles.value?.find(p => p.reconnectTimeout !== -1) ?? null)

// Track whether any opponent was previously offline to suppress outdated prev_player_action message
const hadOfflineOpponent = ref(false)
watch(offlineOpponent, (current, previous) => {
  if (current) {
    hadOfflineOpponent.value = true
  } else if (previous && !current) {
    hadOfflineOpponent.value = true
  }
})

watch(lastGameAction, () => {
  // When a new game action has arrived after reconnect, reset.
  hadOfflineOpponent.value = false
})

const gameActionMessage = computed(() => {
  if (offlineOpponent.value) return `${offlineOpponent.value.username} is offline`
  if (hadOfflineOpponent.value) return ''
  return lastGameAction.value
})
const gameActionPlayerID = computed(() => (offlineOpponent.value ? offlineOpponent.value.id : lastGamePlayerID.value))

const handleCardClicked = (card: CardT) => {
  if (isSelectingCards.value) {
    if (isTradingRandomCard.value) {
      if (cardToExchange.value) {
        cardToExchange.value = null
      } else {
        cardToExchange.value = card
      }
    } else {
      cardToTrade.value = card
      setActiveGameAreaScreen('INITIATE_TRADE_SCREEN')
    }
  }
}

const openCard = (card: CardT) => {
  if (isTurn.value) {
    cardViewModalRef.value?.open(card)
  }
}

const exchangeCard = async () => {
  try {
    deferGameState()
    showLoader()
    await socket.sendEventAsync(GameEvent.DrawCard, { cardId: cardToExchange.value?.id })
    goToGameAreaScreen()
    await sleep(1000)
    resumeGameState()
    canCloseTradeCardOptions.value = true
  } catch (error) {
    handleCustomError(error)
  } finally {
    hideLoader()
  }
}

const initRandomCardExchange = async () => {
  if (isTurn.value) {
    setActiveGameAreaScreen('RANDOM_CARD_EXCHANGE_SELECTION_SCREEN')
  }
}

const handleCardDropped = async (card: Partial<CardT>) => {
  if (!canDragAndDrop.value) return

  try {
    deferGameState()
    showLoader()
    await socket.sendEventAsync(GameEvent.DrawCard, { cardId: card.id })
    playTrashCard()
    await sleep(1000)
    resumeGameState()
    canCloseTradeCardOptions.value = true
  } catch (error) {
    handleCustomError(error)
  } finally {
    hideLoader()
  }
}

const handleCardsReordered = (reorderedCards: CardT[]) => {
  // Store the user's preferred card order using the game ID
  const currentGameId = gameState.value?.id
  if (currentGameId) {
    arrangePlayerHand(reorderedCards)
  }
}

onMounted(() => {
  activateQueue('tradeRandomCard')
})

onBeforeUnmount(() => {
  deactivateQueue('tradeRandomCard')
})
</script>

<style scoped>
@reference "#tailwind";

.card-selection-mode {
  .player-hand {
    @apply z-4;
  }
}
</style>
